import { useEffect, useRef, useState } from 'react';
import {
  ScrollArea,
  Stack,
  Group,
  Switch,
  Select,
  Badge,
  Text,
  Card,
  Indicator,
  Alert,
  Code,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { format } from 'date-fns';

interface LogEntry {
  timestamp: string;
  level: 'info' | 'warning' | 'error' | 'debug';
  source: 'deployment' | 'timoni' | 'k8s' | 'git' | 'health' | 'system';
  message: string;
}

interface LogsViewerProps {
  environmentId: string;
}

export function LogsViewer({ environmentId }: LogsViewerProps) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [autoScroll, setAutoScroll] = useState(true);
  const [levelFilter, setLevelFilter] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);

  // WebSocket connection
  useEffect(() => {
    const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host.replace('pk8s.aops.dev', 'pk8s-api.aops.dev')}/api/environments/${environmentId}/logs/stream`;
    
    const connectWebSocket = () => {
      setConnectionStatus('connecting');
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setConnectionStatus('connected');
      };
      
      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          
          // Backend sends: {type: "log", data: {timestamp, level, source, message}}
          // Extract the actual log data from the nested structure
          const logEntry: LogEntry = parsed.data || parsed;
          
          setLogs((prev) => [...prev, logEntry]);
        } catch (error) {
          console.error('Failed to parse log entry:', error);
          console.error('Raw data was:', event.data);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('disconnected');
      };
      
      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setConnectionStatus('disconnected');
        
        // Attempt to reconnect after 3 seconds
        setTimeout(() => {
          if (wsRef.current === ws) {
            connectWebSocket();
          }
        }, 3000);
      };
      
      wsRef.current = ws;
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [environmentId]);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    }
  }, [logs, autoScroll]);

  // Filter logs
  const filteredLogs = logs.filter((log) => {
    if (levelFilter && log.level !== levelFilter) return false;
    if (sourceFilter && log.source !== sourceFilter) return false;
    return true;
  });

  const getLevelColor = (level: LogEntry['level']) => {
    switch (level) {
      case 'error':
        return 'red';
      case 'warning':
        return 'yellow';
      case 'info':
        return 'blue';
      case 'debug':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getSourceColor = (source: LogEntry['source']) => {
    switch (source) {
      case 'deployment':
        return 'violet';
      case 'timoni':
        return 'cyan';
      case 'k8s':
        return 'blue';
      case 'git':
        return 'orange';
      case 'health':
        return 'green';
      case 'system':
        return 'gray';
      default:
        return 'gray';
    }
  };

  return (
    <Stack gap="md">
      {/* Controls */}
      <Group justify="space-between">
        <Group>
          <Indicator
            color={connectionStatus === 'connected' ? 'green' : connectionStatus === 'connecting' ? 'yellow' : 'red'}
            position="middle-start"
            size={8}
            processing={connectionStatus === 'connecting'}
          >
            <Text size="sm" ml="sm">
              {connectionStatus === 'connected' && 'Connected'}
              {connectionStatus === 'connecting' && 'Connecting...'}
              {connectionStatus === 'disconnected' && 'Disconnected'}
            </Text>
          </Indicator>
          <Text size="sm" c="dimmed">
            {filteredLogs.length} {filteredLogs.length === 1 ? 'entry' : 'entries'}
          </Text>
        </Group>
        <Group>
          <Select
            placeholder="Filter by level"
            clearable
            value={levelFilter}
            onChange={setLevelFilter}
            data={[
              { value: 'info', label: 'Info' },
              { value: 'warning', label: 'Warning' },
              { value: 'error', label: 'Error' },
              { value: 'debug', label: 'Debug' },
            ]}
            style={{ width: 150 }}
          />
          <Select
            placeholder="Filter by source"
            clearable
            value={sourceFilter}
            onChange={setSourceFilter}
            data={[
              { value: 'deployment', label: 'Deployment' },
              { value: 'timoni', label: 'Timoni' },
              { value: 'k8s', label: 'Kubernetes' },
              { value: 'git', label: 'Git' },
              { value: 'health', label: 'Health' },
              { value: 'system', label: 'System' },
            ]}
            style={{ width: 150 }}
          />
          <Switch
            label="Auto-scroll"
            checked={autoScroll}
            onChange={(event) => setAutoScroll(event.currentTarget.checked)}
          />
        </Group>
      </Group>

      {/* Log viewer */}
      <Card withBorder p={0} style={{ height: 600 }}>
        <ScrollArea h="100%" p="md" ref={scrollAreaRef}>
          {filteredLogs.length === 0 ? (
            <Stack align="center" justify="center" h="100%">
              <Text c="dimmed" size="sm">
                {connectionStatus === 'connected'
                  ? 'No logs yet. Waiting for deployment events...'
                  : 'Connecting to log stream...'}
              </Text>
            </Stack>
          ) : (
            <Stack gap="xs">
              {filteredLogs.map((log, index) => (
                <Group key={index} gap="xs" wrap="nowrap" align="flex-start">
                  <Text size="xs" c="dimmed" style={{ minWidth: 80, fontFamily: 'monospace' }}>
                    {(() => {
                      try {
                        const date = new Date(log.timestamp);
                        if (isNaN(date.getTime())) return '--:--:--.---';
                        return format(date, 'HH:mm:ss.SSS');
                      } catch {
                        return '--:--:--.---';
                      }
                    })()}
                  </Text>
                  <Badge size="sm" color={getLevelColor(log.level)} style={{ minWidth: 60 }}>
                    {log.level ? log.level.toUpperCase() : 'UNKNOWN'}
                  </Badge>
                  <Badge size="sm" color={getSourceColor(log.source)} variant="light" style={{ minWidth: 80 }}>
                    {log.source || 'unknown'}
                  </Badge>
                  <Code
                    style={{
                      flex: 1,
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {log.message}
                  </Code>
                </Group>
              ))}
            </Stack>
          )}
        </ScrollArea>
      </Card>

      {/* Info alert for disconnected state */}
      {connectionStatus === 'disconnected' && (
        <Alert icon={<IconInfoCircle />} title="Connection Lost" color="yellow">
          The log stream has been disconnected. Attempting to reconnect...
        </Alert>
      )}
    </Stack>
  );
}

